import { forwardRef } from 'react';

import 'swiper/css/pagination';

import { styled } from '@mui/material';

export const PrevButton = forwardRef<HTMLButtonElement, { tabIndex?: number }>(
  ({ tabIndex = 0 }, ref) => (
    <Button
      className="button-prev"
      ref={ref}
      tabIndex={tabIndex}
      aria-label="previous slide"
    >
      <Icon />
    </Button>
  ),
);

export const NextButton = forwardRef<HTMLButtonElement, { tabIndex?: number }>(
  ({ tabIndex = 0 }, ref) => (
    <Button
      className="button-next"
      ref={ref}
      tabIndex={tabIndex}
      aria-label="next slide"
    >
      <Icon />
    </Button>
  ),
);

export const buttonDisabledClass = 'button-disabled';

const Button = styled('button')(({ theme }) => ({
  position: 'absolute',
  cursor: 'pointer',
  top: '50%',
  transform: 'translateY(-50%)',
  padding: 0,
  border: 'none',
  outline: 'none',
  height: '40px',
  width: '40px',
  color: 'var(--color-white)',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
  display: 'flex',
  backgroundColor: 'transparent',

  svg: {
    color: 'inherit',
    width: '100%',
    height: '100%',
  },

  [theme.breakpoints.up('sm')]: {
    height: '72px',
    width: '72px',
  },

  [theme.breakpoints.up('lg')]: {
    height: '96px',
    width: '96px',
  },

  '&:hover, &:focus-visible': {
    color: 'var(--color-light-red)',
  },

  '&.button-prev': {
    left: 0,

    svg: {
      transform: 'rotate(90deg)',
    },
  },

  '&.button-next': {
    right: 0,

    svg: {
      transform: 'rotate(270deg)',
    },
  },

  [`&.${buttonDisabledClass}`]: {
    display: 'none',
  },
}));

export const PaginationContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'colorTheme',
})<{ colorTheme?: 'light' | 'dark' }>(({ theme, colorTheme = 'light' }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '15px',
  marginTop: 'var(--spacer-l)',
  zIndex: 0,
  position: 'relative',
  pointerEvents: 'auto',

  'div.swiper:has(> &)': {
    '--pagination-height': '25px',
  },

  [theme.breakpoints.up('sm')]: {
    gap: '10px',
    bottom: '0 !important',
  },

  '&.swiper-pagination-horizontal.swiper-pagination-bullets > .swiper-pagination-bullet':
    {
      width: '12px',
      height: '12px',
      margin: 0,
      backgroundColor:
        colorTheme === 'dark' ? 'var(--color-medium-light-grey)' : undefined,
      opacity: colorTheme === 'dark' ? 1 : undefined,

      '&.swiper-pagination-bullet-active': {
        backgroundColor: 'var(--color-light-red)',
      },

      [theme.breakpoints.up('sm')]: {
        width: '10px',
        height: '10px',
      },
    },
}));

const Icon = () => (
  <svg viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24.5002 32.4438L39.974 16.97L38.5598 15.5557L24.5002 29.6154L10.4406 15.5557L9.02637 16.97L24.5002 32.4438Z"
      fill="currentColor"
    />
  </svg>
);
